/**
 * The two CSV exports: the roster, and the on-site-period audit trail.
 *
 * Split into pure builders and one impure download so the escaping is testable
 * without a DOM. It is worth testing: a Czech or Polish crew list is full of
 * names with commas and quotes in them, and a spreadsheet that silently splits
 * "Nowak, Hanna" across two columns produces a compliance record that is wrong
 * in a way nobody reads closely enough to catch.
 */
import { COMPLIANCE_STATUS_LABELS_STANDALONE } from "@gb/contracts";
import type { CrewMember, CrewPeriod } from "@gb/contracts";

/** RFC 4180: double the quotes, and quote anything that needs it. */
export function escapeCell(value: string | number | null | undefined): string {
  const text = value === null || value === undefined ? "" : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * CRLF line endings, per the RFC, because Excel on Windows is the likeliest
 * destination and it is the fussiest reader of the three.
 */
export function toCsv(
  header: readonly string[],
  rows: readonly (readonly (string | number | null)[])[],
): string {
  return [header, ...rows]
    .map((row) => row.map(escapeCell).join(","))
    .join("\r\n");
}

/**
 * How a person's overall state is spelled in an exported record.
 *
 * A cell in a spreadsheet has no pill colour and no roster around it, which is
 * the whole reason this rendering differs from the pill's. The contract holds
 * both, side by side, so the difference stays a decision rather than becoming a
 * divergence.
 */
const COMPLIANCE_WORDS = COMPLIANCE_STATUS_LABELS_STANDALONE;

export const ROSTER_HEADER = [
  "Crew ID",
  "Name",
  "Role",
  "Started",
  "Compliance",
  "Status",
] as const;

/**
 * One row per person, in the order the screen shows them.
 *
 * `Status` distinguishes on-site from onboarding from off-site, because all
 * three can appear in one export and "why is this person in the file twice as
 * different things" is otherwise unanswerable.
 */
export function rosterCsv(groups: {
  onSite: readonly CrewMember[];
  onboarding: readonly CrewMember[];
  offSite: readonly CrewMember[];
}): string {
  const rows = [
    ...groups.onSite.map((p) => row(p, "On site")),
    ...groups.onboarding.map((p) => row(p, "Onboarding")),
    ...groups.offSite.map((p) => row(p, "Off site")),
  ];
  return toCsv(ROSTER_HEADER, rows);
}

function row(person: CrewMember, status: string): (string | number | null)[] {
  return [
    person.id,
    person.name,
    person.roleLabel,
    person.startDate,
    COMPLIANCE_WORDS[person.overall],
    status,
  ];
}

export const PERIODS_HEADER = [
  "Crew ID",
  "Stint",
  "Name",
  "Role",
  "Started",
  "Offboarded",
] as const;

export function periodsCsv(periods: readonly CrewPeriod[]): string {
  return toCsv(
    PERIODS_HEADER,
    periods.map((p) => [
      p.personId,
      `${p.stintNo}/${p.stintTotal}`,
      p.name,
      p.roleLabel,
      p.startDate,
      // "on site" rather than an empty cell: a blank reads as missing data,
      // and this is the most important column in the file.
      p.endDate ?? "on site",
    ]),
  );
}

/** `crew-roster-2026-08-02.csv`. */
export function csvFilename(stem: string, today = new Date()): string {
  return `${stem}-${today.toISOString().slice(0, 10)}.csv`;
}

/**
 * Hand a string to the browser as a file.
 *
 * The object URL is revoked on a timer rather than immediately: Safari has to
 * still be able to read it when the synthetic click is dispatched, and revoking
 * in the same tick cancels the download.
 */
export function downloadCsv(filename: string, csv: string): void {
  // A BOM, so Excel reads the file as UTF-8 rather than as the local codepage.
  // Without it "Łukasz Kamiński" opens as mojibake on a Western-European
  // Windows install, which is most of the people this file is about.
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
